import { useEffect, useRef, useState } from 'react';
import { useCurrentOrg } from '../hooks/useCurrentOrg';
import { getIndustryLabel } from '../lib/industries';
import { Icon } from './Icon';
import { CreateOrgModal } from './CreateOrgModal';

/**
 * Bouton affichant l'organisation active avec un popover listant toutes les
 * organisations de l'utilisateur. Permet de switcher ou d'en créer une nouvelle.
 */
export function OrgSwitcher() {
    const { org, orgs, rolesByOrg, switchOrg, createOrg } = useCurrentOrg();
    const [open, setOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    if (!org) return null;

    const others = orgs.filter((o) => o.id !== org.id);

    return (
        <>
            <div className="org-switcher" ref={containerRef}>
                <button
                    type="button"
                    className={`dash-hero-org is-interactive${open ? ' is-open' : ''}`}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    title={`${org.name} — changer d'organisation`}
                >
                    <span className="dash-hero-org-dot" aria-hidden />
                    <span className="dash-hero-org-name">{org.name}</span>
                    <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                        className={`org-switcher-chev${open ? ' is-open' : ''}`}
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>

                {open && (
                    <div className="org-switcher-popover" role="menu">
                        <div className="org-switcher-head">
                            <span className="org-switcher-eyebrow">Organisation active</span>
                            <div className="org-switcher-current">
                                <span className="org-switcher-avatar" aria-hidden>
                                    {getInitials(org.name)}
                                </span>
                                <div className="org-switcher-current-info">
                                    <strong>{org.name}</strong>
                                    <small>
                                        {getIndustryLabel(org.industry, org.industry_custom)}
                                        {rolesByOrg[org.id] && ` · ${rolesByOrg[org.id]}`}
                                    </small>
                                </div>
                            </div>
                        </div>

                        {others.length > 0 && (
                            <>
                                <div className="org-switcher-sep" />
                                <div className="org-switcher-label">Basculer vers</div>
                                <ul className="org-switcher-list" role="none">
                                    {others.map((o) => (
                                        <li key={o.id} role="none">
                                            <button
                                                type="button"
                                                className="org-switcher-item"
                                                role="menuitem"
                                                onClick={() => {
                                                    switchOrg(o.id);
                                                    setOpen(false);
                                                }}
                                            >
                                                <span className="org-switcher-avatar" aria-hidden>
                                                    {getInitials(o.name)}
                                                </span>
                                                <span className="org-switcher-item-info">
                                                    <strong>{o.name}</strong>
                                                    <small>
                                                        {getIndustryLabel(o.industry, o.industry_custom)}
                                                    </small>
                                                </span>
                                                <Icon name="arrow-right" size={14} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}

                        <div className="org-switcher-sep" />
                        <button
                            type="button"
                            className="org-switcher-add"
                            onClick={() => {
                                setOpen(false);
                                setCreateOpen(true);
                            }}
                        >
                            <span className="org-switcher-add-icon" aria-hidden>
                                <Icon name="plus" size={14} strokeWidth={2.4} />
                            </span>
                            Créer une nouvelle organisation
                        </button>
                    </div>
                )}
            </div>

            <CreateOrgModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSubmit={async (payload) => {
                    await createOrg(payload);
                }}
            />
        </>
    );
}

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    const first = parts[0][0] ?? '';
    const second = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + second).toUpperCase();
}
